/**
 * Configuration management for Healing Hues application
 *
 * This module centralizes all environment-specific configuration,
 * validates required environment variables, and provides helper
 * utilities for environment detection.
 *
 * @module config
 */

/**
 * Reports a missing required environment variable.
 *
 * Never throws: this module is evaluated at import time, before React (and
 * the ErrorBoundary) mounts, so a throw here would blank the whole site.
 * @param {string} key - Environment variable name
 * @param {string} value - Environment variable value
 */
const validateRequired = (key, value) => {
  if (!value) {
    console.error(
      `Missing environment variable: ${key}. ` +
      `Please ensure ${key} is defined in your .env file.`
    );
  }
};

/**
 * Get environment variable with optional validation
 * @param {string} key - Environment variable name
 * @param {boolean} required - Whether to log an error when the variable is missing
 * @param {*} defaultValue - Default value if not set
 * @returns {string|undefined} Environment variable value
 */
const getEnvVar = (key, required = false, defaultValue = undefined) => {
  const value = process.env[key];

  if (required) {
    validateRequired(key, value);
  }

  return value || defaultValue;
};

/**
 * Normalize the API base URL to an origin (or '' for same-origin).
 * Service paths already start with '/api/...', so a base ending in '/api'
 * would produce '/api/api/...'. Strips trailing '/' and a trailing '/api'.
 * @param {string} url - Raw REACT_APP_API_BASE_URL value
 * @returns {string} Normalized base URL
 */
const normalizeApiBaseUrl = (url) => (url || '')
  .trim()
  .replace(/\/+$/, '')
  .replace(/\/api$/i, '')
  .replace(/\/+$/, '');

// Detect current environment
const nodeEnv = process.env.NODE_ENV || 'development';

/**
 * Application configuration object
 * All environment variables are validated and typed here
 */
const config = {
  /**
   * Current environment (development, production, test)
   */
  environment: nodeEnv,

  /**
   * Base URL for backend API: the origin only (e.g. http://localhost:5100),
   * or '' for same-origin behind a proxy. Service paths carry '/api/...'.
   */
  apiBaseUrl: normalizeApiBaseUrl(getEnvVar('REACT_APP_API_BASE_URL', false, '')),

  /**
   * Feature flags (build-time, off unless explicitly 'true')
   */
  features: {
    /**
     * Online payments. Launch default is pay-at-session (false): no payment
     * step is shown and the /api/payments surface is not called.
     */
    paymentsEnabled: process.env.REACT_APP_PAYMENTS_ENABLED === 'true',
  },

  /**
   * Business constants (owner-confirmable; change here, not in components)
   */
  business: {
    timeZone: 'America/Nassau',
    timeZoneLabel: 'Bahamas time',
    contactEmail: 'info@healinghues242.com',
  },

  /**
   * Helper: Check if running in development mode
   */
  isDevelopment: nodeEnv === 'development',

  /**
   * Helper: Check if running in production mode
   */
  isProduction: nodeEnv === 'production',

  /**
   * Helper: Check if running in test mode
   */
  isTest: nodeEnv === 'test',

  /**
   * Sentry Configuration for Error Monitoring
   */
  sentry: {
    dsn: process.env.REACT_APP_SENTRY_DSN || null,
    enabled: process.env.REACT_APP_SENTRY_ENABLED === 'true' || nodeEnv === 'production',
    environment: nodeEnv,
  },
};

// Log configuration in development (helps with debugging)
if (config.isDevelopment) {
  console.log('Application configuration loaded:', {
    environment: config.environment,
    apiBaseUrl: config.apiBaseUrl,
    paymentsEnabled: config.features.paymentsEnabled,
  });
}

export default config;
